import * as Ariakit from '@ariakit/react'
import classnames from 'classnames'
import * as d3 from 'd3'
import { isRedesignEnabled } from 'finviz-charts/app/utils'
import { decodeQueryString } from 'finviz-charts/app/utils/query_string'
import * as React from 'react'
import { Component, RefObject, createRef } from 'react'

import { AdTag, getInvestingChannelPage } from '../../../../../app/shared/components/Ads'
import { Icon } from '../../../../components'
import { RedGreenText } from '../../../../components/RedGreenText'
import { FinancialsTableContextProvider, useFinancialsTableContext } from '../../../../components/financials-table'
import { getValue, setValue } from '../../../../services/local-storage'
import { shortFormatNumber } from '../../../../util'
import { TempRenameSettingsProperties } from '../../../earnings-common/TempRenameSettingsProperties'
import WidgetSettingsSection from '../../../earnings-common/WidgetSettingsSection'
import { EarningsDataEnum, EarningsTimeframeEnum } from '../../../earnings-common/types'
import BarChartBig from '../barChart'
import { DEFAULT_SETTINGS, SETTINGS_COOKIE_KEY, StatementsSourceEnum, StatementsType } from './constants'

interface Bar {
  name: string
  value: number
}

interface BarChartProps {
  data: Bar[]
}

class BarChart extends Component<BarChartProps> {
  svgRef: RefObject<SVGSVGElement>

  constructor(props: BarChartProps) {
    super(props)
    this.svgRef = createRef()
  }

  componentDidMount() {
    this.renderChart()
  }

  componentDidUpdate() {
    this.renderChart()
  }

  renderChart() {
    const data = this.props.data

    const barWidth = 3
    const width = data.length * (barWidth + 1)
    const height = 16

    const x = d3
      .scaleBand()
      .domain(data.map((d) => d.name))
      .range([0, width])

    let min = d3.min(data, (d: Bar) => d.value)!
    if (min < 0) {
      min = d3.min(data, (d: Bar) => Math.min(d.value, -d.value))!
    } else {
      min = 0
    }

    const y = d3
      .scaleLinear()
      .range([height, 0])
      .domain([min, d3.max(data, (d: Bar) => Math.abs(d.value))] as [number, number])
      .nice()
      .unknown(height / 2)

    const svg = d3.select(this.svgRef.current).property('value', [])

    const bars = svg.selectAll('.bar').data(data)
    bars
      .enter()
      .append('rect')
      .attr('class', function (d: Bar) {
        return 'bar bar--' + (d.value < 0 ? 'negative' : 'positive')
      })
      .attr('x', function (d: Bar) {
        return x(d.name)!
      })
      .attr('y', function (d: Bar) {
        return y(Math.max(0, d.value))
      })
      .attr('height', function (d: Bar) {
        let height = Math.abs(y(d.value) - y(0))
        if (d.value > 0 || d.value < 0) {
          height = Math.max(height, 0.5)
        }
        return height
      })
      .attr('width', barWidth)
    bars
      .transition()
      .attr('class', function (d: Bar) {
        return 'bar bar--' + (d.value < 0 ? 'negative' : 'positive')
      })
      .attr('x', function (d: Bar) {
        return x(d.name)!
      })
      .attr('y', function (d: Bar) {
        return y(Math.max(0, d.value))
      })
      .attr('height', function (d: Bar) {
        let height = Math.abs(y(d.value) - y(0))
        if (d.value > 0 || d.value < 0) {
          height = Math.max(height, 0.5)
        }
        return height
      })
      .attr('width', barWidth)
    bars.exit().remove()

    this.svgRef.current!.style.width = width + 'px'
    this.svgRef.current!.style.height = height + 'px'
  }

  render() {
    return <svg ref={this.svgRef} className="quote-bar-chart"></svg>
  }
}

function EliteHoverAd() {
  return (
    <div className="quote-statements-elite-tooltip">
      <div className="quote-statements-elite-tooltip_wrapper">
        <Icon name="lock" width="24" className="mx-auto text-blue-500" />
        <h2 className="quote-statements-elite-subscribe_title">Upgrade your FINVIZ experience</h2>
        <p>
          Join thousands of traders who make more informed decisions with&nbsp;our&nbsp;premium features. Real-time
          quotes, advanced&nbsp;visualizations, historical statements, and much more.
        </p>
        <a
          href="/elite.ashx?utm_source=finviz&utm_medium=banner&utm_campaign=quote-statements"
          target="_blank"
          className="tab-link"
        >
          Learn more
        </a>
      </div>
    </div>
  )
}

interface BarChartWrapperProps {
  data: Bar[]
}

class BarChartWrapper extends Component<BarChartWrapperProps> {
  state = {
    hover: false,
  }

  render() {
    return (
      <div className="tooltip">
        <Ariakit.TooltipProvider placement="right" showTimeout={0}>
          <Ariakit.TooltipAnchor disabled={!this.props.data.some((x) => x.value !== null)}>
            <BarChart data={this.props.data} />
          </Ariakit.TooltipAnchor>
          <Ariakit.Tooltip
            portal={false}
            className={classnames('statements-table_tooltiptext quote_statements-tooltip z-10', {
              'is-ad': !FinvizSettings.hasUserPremium,
            })}
          >
            {FinvizSettings.hasUserPremium ? (
              <BarChartBig data={this.props.data} xAxisRotate={true} />
            ) : (
              <EliteHoverAd />
            )}
          </Ariakit.Tooltip>
        </Ariakit.TooltipProvider>
      </div>
    )
  }
}

const DEFAULT_SOURCE = StatementsSourceEnum.FactSet

const _getDefaultSource = (): StatementsSourceEnum => {
  if (!FinvizSettings.hasUserPremium) {
    return DEFAULT_SOURCE
  }

  return getValue<StatementsSourceEnum>('statements-source', DEFAULT_SOURCE) === StatementsSourceEnum.Reuters
    ? StatementsSourceEnum.Reuters
    : StatementsSourceEnum.FactSet
}

interface StatementsState {
  source: StatementsSourceEnum
  sheet: string
  currency: string | null
  data: Record<string, string[]> | null
}

function StatementsComponent() {
  const { settings } = useFinancialsTableContext<StatementsType>()
  const pageRef = React.useRef(getInvestingChannelPage())

  const [state, setState] = React.useState<StatementsState>({
    source: _getDefaultSource(),
    sheet: 'I',
    currency: null,
    data: null,
  })

  const timeframe = settings.timeframe === EarningsTimeframeEnum.quarterly ? 'Q' : 'A'

  const { data } = state
  const hasData = data && !data.error && Object.keys(data).length > 0
  const hasRedesignEnabled = FinvizSettings?.hasRedesignEnabled

  const _fetch = async () => {
    const { source, sheet } = state
    const { t } = decodeQueryString()

    try {
      const response = await fetch(`/api/statement.ashx?t=${t}&so=${source}&s=${sheet}${timeframe}`)
      const json = await response.json()

      setState({
        ...state,
        currency: json.currency,
        data: json.error ? json : json.data,
      })
    } catch {}
  }

  React.useEffect(() => {
    pageRef.current = getInvestingChannelPage()
    pageRef.current?.create()

    return () => {
      pageRef.current?.destroy()
    }
  }, [])

  React.useEffect(() => {
    void _fetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.sheet, state.source, timeframe])

  const _renderSheetLink = (id: string, text: string) => (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid
    <a
      href="#"
      className={classnames('tab-link', { 'font-bold': state.sheet === id })}
      onClick={(e) => {
        e.preventDefault()
        setState({
          ...state,
          sheet: id,
        })
      }}
    >
      {text}
    </a>
  )

  if (data === null) {
    return null
  }

  return (
    <>
      <table width="100%" cellPadding="3" className="statements-table-header">
        <tbody>
          <tr>
            <td style={FinvizSettings.hasUserPremium ? { paddingLeft: 0 } : {}}>
              {FinvizSettings.hasUserPremium && (
                // TODO fix classname when css mixins removal is merged
                <select
                  className={classnames({
                    'statements-select w-auto': !hasRedesignEnabled,
                    'fv-select': hasRedesignEnabled,
                  })}
                  value={state.source}
                  onChange={(e) => {
                    e.preventDefault()
                    const source =
                      e.target.value === StatementsSourceEnum.Reuters
                        ? StatementsSourceEnum.Reuters
                        : StatementsSourceEnum.FactSet
                    setState({ ...state, source })
                    setValue('statements-source', source, DEFAULT_SOURCE, false)
                  }}
                >
                  <option value={StatementsSourceEnum.FactSet}>FactSet</option>
                  <option value={StatementsSourceEnum.Reuters}>Reuters</option>
                </select>
              )}
              {FinvizSettings.hasUserPremium && <>&nbsp;&nbsp;&nbsp;</>}

              <span className="fullview-links">
                {_renderSheetLink('I', 'Income Statement')} <span className="text-muted-3">•</span>{' '}
                {_renderSheetLink('B', 'Balance Sheet')} <span className="text-muted-3">•</span>{' '}
                {_renderSheetLink('C', 'Cash Flow')}
              </span>
            </td>
            <td align="right">
              <div className="flex justify-end">
                <WidgetSettingsSection
                  isSemiAnnual={false}
                  timeframe={settings.timeframe}
                  showTimeframe
                  hasOldDesign={!isRedesignEnabled()}
                  canShrinkLabels={settings.timeframe === EarningsTimeframeEnum.quarterly}
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
      {hasData && <StatementsTable data={state.data} currency={state.currency} source={state.source} />}
      {!hasData && (
        <table
          width="100%"
          cellPadding="3"
          data-testid="quote-statements-table"
          className={classnames({
            'styled-table-new is-rounded quote_statements-table is-tabular-nums': hasRedesignEnabled,
            'snapshot-table2 has-hovers statements-table': !hasRedesignEnabled,
            'is-free': !FinvizSettings.hasUserPremium,
          })}
        >
          <tbody>
            <tr className="styled-row is-hoverable is-bordered is-rounded is-border-top is-hover-borders">
              <td className={hasRedesignEnabled ? '' : 'snapshot-td2'}>
                The statement is not available. Try switching between FactSet-Reuters source.
              </td>
            </tr>
          </tbody>
        </table>
      )}
    </>
  )
}

function Statements() {
  return (
    <FinancialsTableContextProvider defaultSettings={DEFAULT_SETTINGS} cookieKey={SETTINGS_COOKIE_KEY}>
      <TempRenameSettingsProperties
        defaultSettings={DEFAULT_SETTINGS}
        keysReplace={[
          ['yoyChange', 'mainChange'],
          ['yoyChangePct', 'mainChangePct'],
          ['statementsTimeframe', 'timeframe'],
        ]}
      />
      <StatementsComponent />
    </FinancialsTableContextProvider>
  )
}

function isChartable(label: string) {
  switch (label) {
    case 'Period':
    case 'Period End Date':
    case 'Period Length':
      return false
  }

  return true
}

function parseData(data: Record<string, string[]>, skipFirst: boolean) {
  const parsed: any = {}
  Object.keys(data).forEach((key) => {
    parsed[key] = data[key]
      .map((value, index) => {
        if (isChartable(key)) {
          let parsedValue
          if (value === '') {
            parsedValue = null
          } else {
            parsedValue = parseFloat(value.replace(/,/g, ''))
          }
          return {
            name: data['Period End Date'][index],
            value: parsedValue,
          }
        }
        return null
      })
      .slice(skipFirst ? 1 : 0)
  })

  return parsed as Record<string, Array<{ name: string; value: number }>>
}

function getIsHighlightRow(label: string, source: StatementsSourceEnum) {
  const periodLabel = source === StatementsSourceEnum.FactSet ? 'Period' : 'Period End Date'
  switch (label) {
    case periodLabel:
    case 'Total Revenue':
    case 'Total Premiums Earned':
    case 'Interest Income':
    case 'Gross Profit':
    case 'Total Operating Expense':
    case 'Operating Income':
    case 'Net Income Before Taxes':
    case 'Net Income':
    case 'Total Current Assets':
    case 'Total Assets':
    case 'Total Current Liabilities':
    case 'Total Liabilities':
    case 'Total Equity':
    case 'Total Liabilities and Equity':
    case 'Cash from Operating Activities':
    case 'Cash from Investing Activities':
    case 'Cash from Financing Activities':
    case 'Net Change in Cash':
    case 'Price To Earnings Ratio':
    case 'Price to Book Ratio':
    case 'Price to Free Cash Flow':
    case 'Cash & Short Term Investments':
      return true
  }

  return false
}

type StatementsTableProps = Pick<StatementsState, 'source' | 'data' | 'currency'>

function StatementsTable({ source, data, currency }: StatementsTableProps) {
  const { settings } = useFinancialsTableContext<StatementsType>()
  const skipFirst = [data?.['Period End Date'][0], data?.Period?.[0]].includes('TTM')
  const parsedData = data ? parseData(data, skipFirst) : null
  const isQuarterlyTimeframe = settings.timeframe === EarningsTimeframeEnum.quarterly

  const yoyData = React.useMemo(
    () =>
      parsedData
        ? Object.entries(parsedData).reduce<
            Record<
              string,
              {
                change: Array<number | null>
                changePct: Array<number | null>
                yoyOnQoqChange: Array<number | null>
                yoyOnQoqChangePct: Array<number | null>
              }
            >
          >((acc, [key, values]) => {
            if (!isChartable(key)) {
              return acc
            }
            const yoyData: Array<{
              change: number | null
              changePct: number | null
              yoyOnQoqChange: number | null
              yoyOnQoqChangePct: number | null
            }> = []
            let prevValue: number | null = null

            // values are in DESC order
            const reversedValues = [...values].reverse()

            reversedValues.forEach(({ value }, index) => {
              const isValidValue = Number.isFinite(value)
              const isValidPrevValue = Number.isFinite(prevValue)

              let yoyOnQoqChange = null
              let yoyOnQoqChangePct = null
              let change = null
              let changePct = null

              if (isValidValue && isValidPrevValue) {
                change = value - prevValue!
                changePct = prevValue !== 0 ? (change / Math.abs(prevValue!)) * 100 : null
              }

              if (isQuarterlyTimeframe) {
                const prevQt = reversedValues[index - 4]
                yoyOnQoqChange = isValidValue && Number.isFinite(prevQt?.value) ? value - prevQt!.value : null
                yoyOnQoqChangePct =
                  prevQt?.value && yoyOnQoqChange !== null ? (yoyOnQoqChange / Math.abs(prevQt.value)) * 100 : null
              }

              yoyData.push({
                change,
                changePct,
                yoyOnQoqChange,
                yoyOnQoqChangePct,
              })

              prevValue = value
            })

            const reversedYoyData = [...yoyData].reverse()
            return {
              ...acc,
              [key]: {
                change: reversedYoyData.map(({ change }) => change),
                changePct: reversedYoyData.map(({ changePct }) => changePct),
                yoyOnQoqChange: reversedYoyData.map(({ yoyOnQoqChange }) => yoyOnQoqChange),
                yoyOnQoqChangePct: reversedYoyData.map(({ yoyOnQoqChangePct }) => yoyOnQoqChangePct),
              },
            }
          }, {})
        : {},
    [parsedData, isQuarterlyTimeframe]
  )

  if (!data || !parsedData) return null

  const hasRedesignEnabled = FinvizSettings?.hasRedesignEnabled
  const cellClass = classnames('align-top', {
    'snapshot-td2 h-6 !leading-4 !pt-[4px]': !hasRedesignEnabled,
  })
  let adRowIndexStart = !hasRedesignEnabled && data['Period Length'] ? 2 : 1
  adRowIndexStart += data['Period'] ? 1 : 0
  const yoyGrowthLabel = isQuarterlyTimeframe ? 'QoQ Growth' : 'YoY Growth'

  return (
    <>
      <table
        width="100%"
        cellPadding="3"
        cellSpacing="0"
        data-testid="quote-statements-table"
        className={classnames({
          'styled-table-new is-rounded quote_statements-table is-tabular-nums': hasRedesignEnabled,
          'snapshot-table2 has-hovers statements-table': !hasRedesignEnabled,
          'is-free': !FinvizSettings.hasUserPremium,
        })}
      >
        <tbody>
          {Object.keys(data).map((key, rowIndex) => {
            const hasYoYData = !!yoyData[key] && data[key].some((value) => value)
            const isHighlightRow = getIsHighlightRow(key, source)
            return (
              <tr
                key={key}
                className={classnames({
                  'styled-row is-hoverable is-bordered is-rounded is-border-top is-hover-borders': hasRedesignEnabled,
                  'is-highlighted': hasRedesignEnabled && isHighlightRow,
                  'table-dark-row': !hasRedesignEnabled && isHighlightRow,
                  'table-light-row': !hasRedesignEnabled && !isHighlightRow,
                  'first-row': rowIndex === 0,
                  'elite-row': rowIndex === adRowIndexStart,
                })}
              >
                <td className={cellClass}>
                  {key}
                  {hasYoYData && isQuarterlyTimeframe && settings[EarningsDataEnum.extraChangeYoYonQoQ] && (
                    <>
                      <br />
                      <span className="text-muted-2">YoY Growth</span>
                    </>
                  )}
                  {hasYoYData && isQuarterlyTimeframe && settings[EarningsDataEnum.extraChangePctYoYonQoQ] && (
                    <>
                      <br />
                      <span className="text-muted-2">YoY Growth %</span>
                    </>
                  )}
                  {hasYoYData && settings[EarningsDataEnum.mainChange] && (
                    <>
                      <br />
                      <span className="text-muted-2">{yoyGrowthLabel}</span>
                    </>
                  )}
                  {hasYoYData && settings[EarningsDataEnum.mainChangePct] && (
                    <>
                      <br />
                      <span className="text-muted-2">{`${yoyGrowthLabel} %`}</span>
                    </>
                  )}
                </td>
                <td className={cellClass} align="center">
                  {isChartable(key) && <BarChartWrapper data={parsedData[key].slice(0, 8).reverse()} />}
                </td>
                {data[key].slice(0, 8).map((value, index) => {
                  const changeIndex = skipFirst ? index - 1 : index
                  const yoyChangeValue = yoyData[key]?.change[changeIndex] ?? null
                  const yoyChangePctValue = yoyData[key]?.changePct[changeIndex] ?? null
                  const yoyOnQoqChangeValue = yoyData[key]?.yoyOnQoqChange[changeIndex] ?? null
                  const yoyOnQoqChangePctValue = yoyData[key]?.yoyOnQoqChangePct[changeIndex] ?? null

                  let formattedValue = value === '' ? null : value
                  if (source === StatementsSourceEnum.FactSet && key === 'Period') {
                    if (isQuarterlyTimeframe && value.includes('Q')) {
                      const [year, quarterNumber] = value.split('Q')
                      formattedValue = `Q${quarterNumber} ${year}`
                    } else if (value.includes('FY')) {
                      const [year] = value.split('FY')
                      formattedValue = `FY ${year}`
                    }
                  }

                  if (!FinvizSettings.hasUserPremium) {
                    if (rowIndex === adRowIndexStart && index === 3) {
                      return (
                        <td
                          key={index}
                          align="right"
                          className="quote-statements-elite-subscribe align-middle"
                          rowSpan={Object.keys(data).length - 1}
                          colSpan={data[key].length - 3}
                        >
                          <AdTag
                            name="IC_Statements"
                            className="mx-auto -mt-32 mb-15 text-center text-2xs text-blue-500"
                          />
                          <Icon name="lock" width="24" className="mx-auto text-blue-500" />
                          <h2 className="quote-statements-elite-subscribe_title">Upgrade your FINVIZ experience</h2>
                          <p className="quote-statements-elite-subscribe_text">
                            Join thousands of traders who make more informed decisions with&nbsp;our&nbsp;premium
                            features. Real-time quotes, advanced&nbsp;visualizations, historical statements, and much
                            more.
                          </p>
                          <a
                            href="/elite.ashx?utm_source=finviz&utm_medium=banner&utm_campaign=quote-statements"
                            target="_blank"
                            className="tab-link"
                          >
                            Learn more
                          </a>
                        </td>
                      )
                    }
                    if (
                      index > 2 &&
                      ((hasRedesignEnabled && !['Period End Date', 'Period'].includes(key)) || isChartable(key))
                    ) {
                      return null
                    }
                  }

                  return (
                    <td key={index} align="right" className={cellClass}>
                      <span className={classnames({ 'text-muted-2': formattedValue === null })}>
                        {Number.isFinite(formattedValue) || formattedValue === null
                          ? shortFormatNumber(formattedValue as number | null, { defaultValue: '—' })
                          : formattedValue}
                      </span>
                      {hasYoYData && isQuarterlyTimeframe && settings[EarningsDataEnum.extraChangeYoYonQoQ] && (
                        <>
                          <br />
                          <RedGreenText value={yoyOnQoqChangeValue} rightContent={null}>
                            {shortFormatNumber(yoyOnQoqChangeValue, { defaultValue: '—' })}
                          </RedGreenText>
                        </>
                      )}
                      {hasYoYData && isQuarterlyTimeframe && settings[EarningsDataEnum.extraChangePctYoYonQoQ] && (
                        <>
                          <br />
                          <RedGreenText
                            value={yoyOnQoqChangePctValue}
                            rightContent={yoyOnQoqChangePctValue !== null ? '%' : null}
                          >
                            {yoyOnQoqChangePctValue?.toFixed(2) ?? '—'}
                          </RedGreenText>
                        </>
                      )}
                      {hasYoYData && settings[EarningsDataEnum.mainChange] && (
                        <>
                          <br />
                          <RedGreenText value={yoyChangeValue} rightContent={null}>
                            {shortFormatNumber(yoyChangeValue, { defaultValue: '—' })}
                          </RedGreenText>
                        </>
                      )}
                      {hasYoYData && settings[EarningsDataEnum.mainChangePct] && (
                        <>
                          <br />
                          <RedGreenText
                            value={yoyChangePctValue}
                            rightContent={yoyChangePctValue !== null ? '%' : null}
                          >
                            {yoyChangePctValue?.toFixed(2) ?? '—'}
                          </RedGreenText>
                        </>
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
      <table className="quote_statements-footer" width="100%" cellPadding="3">
        <tbody>
          <tr>
            <td align="right">values in Millions, {currency}</td>
          </tr>
        </tbody>
      </table>
    </>
  )
}

export default Statements
